import { CalculationService } from '../calculation.service';
import {
  MFRS_ACCOUNTING_STANDARD,
  MPERS_ACCOUNTING_STANDARD,
} from './schema-picker.util';

/**
 * AS-842 — Calculation-mismatch detection for the silent-validation path.
 *
 * The "Validated*" / Success* warning surface in the editor is driven by
 * `validateAgainstApiData()`: it compares each calculated total in the form
 * against an "extracted" baseline (what N8N originally returned), and flips
 * the row to `Validated*` when they differ.
 *
 * The pure-validator path needs the same signal but with a smaller surface:
 * for a fresh `PendingValidation` row, the persisted DB values ARE the N8N
 * extraction, and recomputing the totals from the line items reveals the
 * same mismatches the editor would surface to a user who opened the row.
 *
 * Why the calculator is a module-level instance, not DI-injected:
 *
 * `CalculationService` is `@Injectable` for ergonomic DI inside components,
 * but it is a stateless bag of pure-ish methods — empty constructor, no
 * service dependencies. Pure validators are required to be free of Angular
 * DI per `SheetValidator` contract (see `sheet-validator.ts`'s `validate()`
 * comment), so a single module-level instance keeps validators pure while
 * avoiding the 4400-line refactor of moving every method out of the class.
 */
export const calculator = new CalculationService();

/**
 * Round to 2 decimal places to neutralise floating-point arithmetic before
 * equality. Mirrors the editor's `validateAgainstApiData` rounding so the
 * silent path agrees with the mounted path.
 *
 * Returns `null` when the input cannot be coerced — callers treat null as
 * "skip this cell" (matches the editor's `baselineNum !== null` guard).
 */
export function round2(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const num = typeof value === 'number' ? value : parseFloat(String(value));
  if (Number.isNaN(num) || !Number.isFinite(num)) return null;
  return Math.round(num * 100) / 100;
}

/**
 * Compare the persisted slice (as returned by `RetrieveMbrsDataService`)
 * against a recomputed map (as returned by any
 * `CalculationService.calculateTotals_*`) and return `true` if any
 * calculated key disagrees beyond rounding.
 *
 * Skip rules (mirroring the editor):
 *   - If the persisted value is null/undefined/'' → skip. This matches the
 *     editor's "no baseline → no mismatch" posture: when the persisted side
 *     is empty there is nothing to compare against.
 *   - If both sides round to the same value → skip.
 *
 * Returns `true` on the first mismatch (short-circuit) so the caller can
 * cheaply convert "any mismatch" into a single boolean.
 */
export function hasCalculatedFieldMismatch(
  persisted: Record<string, any>,
  recomputed: Record<string, any>
): boolean {
  for (const [key, recomputedValue] of Object.entries(recomputed)) {
    const recomputedNum = round2(recomputedValue);
    if (recomputedNum === null) continue;

    const persistedNum = round2(persisted[key]);
    if (persistedNum === null) continue;

    if (persistedNum !== recomputedNum) return true;
  }
  return false;
}

/**
 * Build the snapshot the equality checks must compare against: the persisted
 * payload with every freshly-recomputed calculated total overlaid on top.
 *
 * Why this exists (the parity bug it closes):
 *   The mounted editor calls `calculateTotals()` and patches the results into
 *   the form BEFORE running its equality validation. The headless (pure)
 *   validators historically compared the calculated operands straight from the
 *   persisted (N8N-extracted) payload, WITHOUT replaying the calculation. When
 *   the extracted total disagreed with the line-item sum (e.g. a balance sheet
 *   whose printed `Assets` ties to `EquityAndLiabilities` but whose extracted
 *   line items don't reconcile), the two paths reached opposite verdicts — the
 *   listing icon showed a green tick while opening the sheet showed a red
 *   cross. Overlaying the recomputed totals here makes the headless equality
 *   compare the SAME operands the editor does, so the blocking accounting
 *   tie-outs (Assets == EquityAndLiabilities, ProfitLoss == ProfitLoss_2,
 *   OtherComprehensiveIncome/ComprehensiveIncome vs role 15, …) agree.
 *
 * `recomputed` (as returned by any `CalculationService.calculateTotals_*`)
 * contains ONLY the calculated-total keys, so the spread overrides just those
 * cells and leaves every editable persisted field intact.
 *
 * The original `persisted` object is NOT mutated: callers keep it untouched to
 * feed `hasCalculatedFieldMismatch(persisted, recomputed)`, which still needs
 * the raw extraction to drive the non-blocking `Validated*` asterisk (the
 * "sum vs N8N-extracted" suggested-change surface, kept distinct from the
 * blocking equality cross).
 */
export function buildEqualitySnapshot(
  persisted: Record<string, any>,
  recomputed: Record<string, any>
): Record<string, any> {
  return { ...persisted, ...recomputed };
}

/**
 * The four `dualColumn` cell suffixes every Statement-style sheet stores its
 * (dimension × period) values under. Inlined here (rather than importing each
 * sheet's `buildRole*ColumnKey`) because the bracket format is a cross-sheet
 * invariant and the editor's own `pullRole*Values()` methods hard-code the
 * exact same suffix list.
 */
const DUAL_COLUMN_CELL_SUFFIXES: ReadonlyArray<string> = [
  '[Consolidated][CurrentYear]',
  '[Consolidated][PreviousYear]',
  '[Separate][CurrentYear]',
  '[Separate][PreviousYear]',
];

/** Minimal shape of a schema control carrying a cross-sheet `linkTo`. */
export interface LinkableControl {
  qname: string;
  ui?: { linkTo?: { role: string; qname: string } };
}

/**
 * Replay a sheet's `pullRole*Values()` inheritance in the headless path:
 * return a shallow copy of `persisted` with every control whose
 * `ui.linkTo.role === linkRole` overwritten by `sourceData`'s value for the
 * same (dimension × period) cell.
 *
 * Why this exists (the parity bug it closes):
 *   Statement sheets inherit leaf line items from a sub-sheet at MOUNT time —
 *   e.g. Role-8 pulls Role-9 sub-classification totals (pullRole9Values), and
 *   Role-12 pulls Role-13 analysis-of-P&L totals (pullRole13Values) — BEFORE
 *   they call calculateTotals() and validate. The headless validators recompute
 *   from the host sheet's PERSISTED slice alone, so when the sub-sheet's value
 *   disagrees with the host's persisted leaf, only the mounted recompute shifts.
 *   That broke the host sheet's blocking equality tie-outs only after the user
 *   opened the sheet (green tick on the listing → red cross on open). Overlaying
 *   the inherited leaves here makes the headless recompute see the SAME operands
 *   the editor does.
 *
 * Mirrors the editor's pull methods verbatim:
 *  - only controls whose `ui.linkTo.role` matches `linkRole` inherit;
 *  - all four (dimension × period) cells are considered, regardless of column
 *    visibility (the editor pulls every column too);
 *  - a source cell is applied only when it is neither null nor undefined (the
 *    editor's `value !== undefined && value !== null` guard), so a sparse source
 *    slice never blanks a populated host leaf.
 *
 * Returns `persisted` untouched (never mutated) when there is no source slice
 * to inherit from — tolerated-absent, matching the editor's empty-slice early
 * return. Callers keep the raw `persisted` object for the Validated*
 * `hasCalculatedFieldMismatch` diff (which must see the raw N8N extraction).
 */
export function applyLinkedInheritance(
  controls: ReadonlyArray<LinkableControl>,
  persisted: Record<string, any>,
  sourceData: Record<string, any> | undefined,
  linkRole: string
): Record<string, any> {
  if (!sourceData || Object.keys(sourceData).length === 0) {
    return persisted;
  }

  const inherited: Record<string, any> = { ...persisted };

  for (const control of controls) {
    const linkTo = control.ui?.linkTo;
    if (!linkTo || linkTo.role !== linkRole) continue;

    for (const suffix of DUAL_COLUMN_CELL_SUFFIXES) {
      const sourceValue = sourceData[`${linkTo.qname}${suffix}`];
      if (sourceValue === undefined || sourceValue === null) continue;
      inherited[`${control.qname}${suffix}`] = sourceValue;
    }
  }

  return inherited;
}

/**
 * Translate the orchestrator's `accountingStandard` context value into the
 * XBRL prefix `CalculationService` expects. Mirrors
 * `role8AccountingStandardToTaxonomy` + the role components' `isMFRS()`
 * check, kept here so each validator does not re-derive it.
 *
 * Anything other than the MPERS sentinel
 * ("Malaysian Private Entities Reporting Standards" — see
 * `ROLE8_MPERS_ACCOUNTING_STANDARD` in role8 rules.ts for the upstream
 * source) defaults to `'ifrs-full'` (MFRS) — matches the existing
 * fallback in the role components and `role8AccountingStandardToTaxonomy`.
 */
export function accountingStandardToXbrlPrefix(
  accountingStandard: string | undefined | null
): 'ifrs-full' | 'ifrs-smes' {
  return accountingStandard === MPERS_ACCOUNTING_STANDARD
    ? 'ifrs-smes'
    : 'ifrs-full';
}

/**
 * Is `accountingStandard` one of the two EXACT sentinels the backend emits
 * (`MFRS_ACCOUNTING_STANDARD` / `MPERS_ACCOUNTING_STANDARD`)?
 *
 * Why the calc-diff path needs this guard (the false `Success *` it closes):
 *
 * `accountingStandardToXbrlPrefix` (and `pickSchema`) are **MFRS-default** — any
 * value other than the exact MPERS literal, *including `''`/`null`/`undefined`*,
 * selects the MFRS (`ifrs-full`) calculator. That is correct for schema picking
 * (a sheet must render *some* schema), but it is WRONG for the non-blocking
 * calc-diff recompute: when a conversion's filing-info has not hydrated yet the
 * context's `accountingStandard` is `''`, so an MPERS sheet would be recomputed
 * with the MFRS calculator. The prefix-dependent leaves then resolve to `0`
 * (e.g. `ifrs-full:CashOnHand` is absent from an `ifrs-smes` slice), the
 * recomputed total diverges from the N8N-extracted total, and a phantom
 * `hasSuggestedChanges` (`Success *`) is raised that vanishes the instant the
 * sheet is opened with a resolved standard.
 *
 * The taxonomy-equality guard (`schemaTaxonomy === contextTaxonomy`) cannot
 * catch this: an empty standard defaults BOTH sides to MFRS, so they spuriously
 * "agree". The only reliable signal is "is the standard a recognised sentinel" —
 * this predicate. Calc-diff validators gate `hasSuggestedChanges` on it, so the
 * asterisk is computed only when the taxonomy is genuinely known (a real MFRS
 * or MPERS filing) and is suppressed (non-blocking) while it is unresolved.
 */
export function isResolvedAccountingStandard(accountingStandard: string | undefined | null): boolean {
  return (accountingStandard === MFRS_ACCOUNTING_STANDARD || accountingStandard === MPERS_ACCOUNTING_STANDARD);
}
